import { useMemo, useState, type ReactNode } from "react";
import { cn } from "@web/lib/utils.ts";
import { formatDate, formatDateTime } from "@web/lib/format.ts";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Prefer a friendly label from common MusicBrainz keys. */
const LABEL_MAP: Record<string, string> = {
  name: "Name",
  title: "Title",
  type: "Type",
  country: "Country",
  disambiguation: "Disambiguation",
  "sort-name": "Sort name",
  gender: "Gender",
  area: "Area",
  "begin-area": "Begin area",
  "end-area": "End area",
  "life-span": "Life span",
  aliases: "Aliases",
  tags: "Tags",
  genres: "Genres",
  relations: "Relations",
  "artist-credit": "Artist credit",
  "release-group": "Release group",
  releases: "Releases",
  recordings: "Recordings",
  media: "Media",
  isrcs: "ISRCs",
  annotation: "Annotation",
  rating: "Rating",
  length: "Length",
  status: "Status",
  packaging: "Packaging",
  date: "Date",
  barcode: "Barcode",
  language: "Language",
  script: "Script",
  "first-release-date": "First release",
  "primary-type": "Primary type",
  "secondary-types": "Secondary types",
  ipis: "IPIs",
  isnis: "ISNIs",
};

function humanLabel(key: string): string {
  return LABEL_MAP[key] ?? key.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function mbUrl(type: string, mbid: string): string {
  return `https://musicbrainz.org/${type}/${mbid}`;
}

function guessMbType(obj: Record<string, unknown>, fallback: string): string {
  if (typeof obj["entity-type"] === "string") return obj["entity-type"];
  if ("title" in obj && "artist-credit" in obj && !("media" in obj)) return "recording";
  if ("title" in obj && "media" in obj) return "release";
  if ("title" in obj && "primary-type" in obj) return "release-group";
  if ("name" in obj && ("life-span" in obj || "type" in obj || "country" in obj)) return "artist";
  if ("name" in obj && "type" in obj && !("country" in obj)) return "work";
  return fallback;
}

function displayName(obj: Record<string, unknown>): string | null {
  if (typeof obj.name === "string" && obj.name) return obj.name;
  if (typeof obj.title === "string" && obj.title) return obj.title;
  return null;
}

function formatScalar(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    // MusicBrainz recording length is ms
    if (value > 1000 && value < 3_600_000) {
      const sec = Math.round(value / 1000);
      return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
    }
    return value.toLocaleString();
  }
  if (typeof value === "string") {
    // ISO date / datetime / partial MB date
    if (/^\d{4}(-\d{2}){0,2}(T|$)/.test(value)) {
      return value.includes("T") ? formatDateTime(value) : formatDate(value);
    }
  }
  return String(value);
}

function lifeSpanText(span: unknown): string | null {
  if (!isPlainObject(span)) return null;
  const begin = typeof span.begin === "string" ? formatDate(span.begin) : null;
  const end = typeof span.end === "string" ? formatDate(span.end) : null;
  if ((!begin || begin === "—") && (!end || end === "—")) return null;
  const b = begin && begin !== "—" ? begin : null;
  const e = end && end !== "—" ? end : null;
  if (b && e) return `${b} – ${e}`;
  if (b) return `${b} –`;
  return `– ${e}`;
}

function areaName(area: unknown): string | null {
  if (!isPlainObject(area)) return null;
  return typeof area.name === "string" ? area.name : null;
}

function artistCreditText(credit: unknown): string | null {
  if (!Array.isArray(credit)) return null;
  const parts: string[] = [];
  for (const c of credit) {
    if (!isPlainObject(c)) continue;
    const name =
      (typeof c.name === "string" && c.name) ||
      (isPlainObject(c.artist) && typeof c.artist.name === "string" ? c.artist.name : null);
    if (name) parts.push(name);
    if (typeof c.joinphrase === "string") parts.push(c.joinphrase);
  }
  const text = parts.join("");
  return text || null;
}

type TagLike = { name: string; count?: number };

function readTags(value: unknown): TagLike[] {
  if (!Array.isArray(value)) return [];
  const out: TagLike[] = [];
  for (const t of value) {
    if (!isPlainObject(t) || typeof t.name !== "string") continue;
    out.push({
      name: t.name,
      count: typeof t.count === "number" ? t.count : undefined,
    });
  }
  return out;
}

/**
 * Pull the entity object from stored raw_json. Track enrich may store the whole
 * ISRC payload — unwrap the recording that matches `mbid` when needed.
 */
export function unwrapMbEntity(
  raw: unknown,
  entityType: string,
  mbid: string | null,
): Record<string, unknown> | null {
  if (!isPlainObject(raw)) return null;

  if (entityType === "recording" && Array.isArray(raw.recordings) && mbid) {
    const hit = raw.recordings.find(
      (r) => isPlainObject(r) && r.id === mbid,
    );
    if (isPlainObject(hit)) return hit;
    const first = raw.recordings[0];
    if (isPlainObject(first)) return first;
  }

  if (typeof raw.id === "string" || "name" in raw || "title" in raw) {
    return raw;
  }

  // ISRC envelope without a match — still show first recording
  if (Array.isArray(raw.recordings) && isPlainObject(raw.recordings[0])) {
    return raw.recordings[0] as Record<string, unknown>;
  }

  return raw;
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  if (children === null || children === undefined || children === "") return null;
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="shrink-0 text-xs text-faint">{label}</span>
      <div className="min-w-0 text-right text-sm text-text">{children}</div>
    </div>
  );
}

function ExternalMbLink({
  type,
  mbid,
  label,
}: {
  type: string;
  mbid: string;
  label: string;
}) {
  return (
    <a
      href={mbUrl(type, mbid)}
      target="_blank"
      rel="noreferrer"
      className="text-accent hover:underline"
    >
      {label}
    </a>
  );
}

function ChipRow({ items }: { items: TagLike[] }) {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap justify-end gap-1">
      {items.slice(0, 24).map((t) => (
        <span
          key={t.name}
          className="rounded bg-panel-elevated px-1.5 py-0.5 text-xs text-muted"
          title={t.count != null ? `${t.count}` : undefined}
        >
          {t.name}
        </span>
      ))}
      {items.length > 24 && (
        <span className="text-xs text-faint">+{items.length - 24}</span>
      )}
    </div>
  );
}

/** collapsed → open (body only) → full (body + nested expanded) → collapsed */
type SectionMode = "collapsed" | "open" | "full";

function nextSectionMode(mode: SectionMode, triState: boolean): SectionMode {
  if (!triState) return mode === "collapsed" ? "open" : "collapsed";
  if (mode === "collapsed") return "open";
  if (mode === "open") return "full";
  return "collapsed";
}

function sectionModeLabel(mode: SectionMode, triState: boolean): string {
  if (mode === "collapsed") return "Show";
  if (triState && mode === "open") return "Expand";
  return "Hide";
}

function CollapsibleSection({
  title,
  count,
  defaultOpen = false,
  triState = false,
  children,
}: {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  /** When true, header cycles collapsed → open → full (expand nested). */
  triState?: boolean;
  children: ReactNode | ((mode: "open" | "full") => ReactNode);
}) {
  const [mode, setMode] = useState<SectionMode>(defaultOpen ? "open" : "collapsed");
  return (
    <div className="border-t border-line">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 py-2 text-left"
        onClick={() => setMode((m) => nextSectionMode(m, triState))}
      >
        <span className="text-xs font-semibold tracking-wider text-faint uppercase">
          {title}
          {count != null ? ` · ${count}` : ""}
        </span>
        <span className="text-xs text-muted">{sectionModeLabel(mode, triState)}</span>
      </button>
      {mode !== "collapsed" && (
        <div className="pb-2">
          {typeof children === "function" ? children(mode) : children}
        </div>
      )}
    </div>
  );
}

function RelationItem({ rel }: { rel: Record<string, unknown> }) {
  const targetType =
    typeof rel["target-type"] === "string" ? rel["target-type"] : null;
  const targetKey = targetType ?? "artist";
  const target = isPlainObject(rel[targetKey])
    ? (rel[targetKey] as Record<string, unknown>)
    : null;
  const targetId = target && typeof target.id === "string" ? target.id : null;
  const label = target ? displayName(target) : null;
  const attrs = Array.isArray(rel.attributes)
    ? rel.attributes.filter((a): a is string => typeof a === "string").join(", ")
    : "";

  return (
    <li className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
      <span className="min-w-0 truncate text-right">
        {targetId && label && UUID_RE.test(targetId) ? (
          <ExternalMbLink type={targetKey} mbid={targetId} label={label} />
        ) : (
          <span className="text-text">{label ?? "—"}</span>
        )}
        {attrs && <span className="ml-1 text-xs text-faint">({attrs})</span>}
      </span>
    </li>
  );
}

function RelationTypeGroup({
  type,
  items,
  forceOpen,
}: {
  type: string;
  items: Record<string, unknown>[];
  forceOpen: boolean;
}) {
  const [open, setOpen] = useState(forceOpen);
  const [prevForceOpen, setPrevForceOpen] = useState(forceOpen);
  if (prevForceOpen !== forceOpen) {
    setPrevForceOpen(forceOpen);
    setOpen(forceOpen);
  }
  return (
    <div className="border-t border-line first:border-t-0">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 py-1.5 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-xs capitalize text-muted">
          {type}
          <span className="text-faint"> · {items.length}</span>
        </span>
        <span className="text-xs text-faint">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <ul className="pb-1 pl-2 divide-y divide-line">
          {items.map((rel, i) => (
            <RelationItem key={i} rel={rel} />
          ))}
        </ul>
      )}
    </div>
  );
}

function RelationRows({
  relations,
  expandAll,
}: {
  relations: unknown;
  expandAll: boolean;
}) {
  const groups = useMemo(() => {
    if (!Array.isArray(relations) || relations.length === 0) return [];
    const map = new Map<string, Record<string, unknown>[]>();
    for (const rel of relations) {
      if (!isPlainObject(rel)) continue;
      const type = typeof rel.type === "string" ? rel.type : "related";
      const list = map.get(type);
      if (list) list.push(rel);
      else map.set(type, [rel]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [relations]);

  if (groups.length === 0) {
    return <p className="text-xs text-faint">None</p>;
  }

  return (
    <div>
      {groups.map(([type, items]) => (
        <RelationTypeGroup
          key={type}
          type={type}
          items={items}
          forceOpen={expandAll}
        />
      ))}
    </div>
  );
}

function AliasRows({ aliases }: { aliases: unknown }) {
  if (!Array.isArray(aliases) || aliases.length === 0) {
    return <p className="text-xs text-faint">None</p>;
  }
  return (
    <ul className="divide-y divide-line">
      {aliases.map((a, i) => {
        if (!isPlainObject(a) || typeof a.name !== "string") return null;
        const locale = typeof a.locale === "string" ? a.locale : null;
        const type = typeof a.type === "string" ? a.type : null;
        return (
          <li key={i} className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
            <span className="min-w-0 truncate text-text">{a.name}</span>
            <span className="shrink-0 text-xs text-faint">
              {[type, locale].filter(Boolean).join(" · ") || "—"}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function NestedEntityList({
  items,
  fallbackType,
}: {
  items: unknown;
  fallbackType: string;
}) {
  if (!Array.isArray(items) || items.length === 0) {
    return <p className="text-xs text-faint">None</p>;
  }
  return (
    <ul className="divide-y divide-line">
      {items.slice(0, 40).map((item, i) => {
        if (!isPlainObject(item)) return null;
        const id = typeof item.id === "string" ? item.id : null;
        const label = displayName(item) ?? id ?? "—";
        const type = guessMbType(item, fallbackType);
        const extra =
          typeof item.date === "string"
            ? item.date
            : typeof item.country === "string"
              ? item.country
              : null;
        return (
          <li key={id ?? i} className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
            <span className="min-w-0 truncate">
              {id && UUID_RE.test(id) ? (
                <ExternalMbLink type={type} mbid={id} label={label} />
              ) : (
                label
              )}
            </span>
            {extra && <span className="shrink-0 text-xs text-faint">{extra}</span>}
          </li>
        );
      })}
      {items.length > 40 && (
        <li className="py-1.5 text-xs text-faint">+{items.length - 40} more</li>
      )}
    </ul>
  );
}

function AnnotationText({ value }: { value: unknown }) {
  if (typeof value === "string" && value.trim()) {
    return <p className="whitespace-pre-wrap text-left text-sm text-muted">{value}</p>;
  }
  if (isPlainObject(value) && typeof value.text === "string") {
    return <p className="whitespace-pre-wrap text-left text-sm text-muted">{value.text}</p>;
  }
  return null;
}

/**
 * Readable MusicBrainz payload: facts up top, expandable groups, clickable MB links.
 * Raw JSON is not rendered here — EnrichSection keeps that collapsed underneath.
 */
export function MbPrettyView({
  raw,
  entityType,
  mbid,
}: {
  raw: unknown;
  entityType: string;
  mbid: string | null;
}) {
  const entity = useMemo(
    () => unwrapMbEntity(raw, entityType, mbid),
    [raw, entityType, mbid],
  );

  if (!entity) {
    return <p className="text-sm text-muted">No MusicBrainz data stored.</p>;
  }

  const name = displayName(entity);
  const type = typeof entity.type === "string" ? entity.type : null;
  const country = typeof entity.country === "string" ? entity.country : null;
  const disambig =
    typeof entity.disambiguation === "string" ? entity.disambiguation : null;
  const gender = typeof entity.gender === "string" ? entity.gender : null;
  const life = lifeSpanText(entity["life-span"]);
  const area = areaName(entity.area);
  const beginArea = areaName(entity["begin-area"]);
  const endArea = areaName(entity["end-area"]);
  const credit = artistCreditText(entity["artist-credit"]);
  const length =
    typeof entity.length === "number" ? formatScalar(entity.length) : null;
  const date =
    typeof entity.date === "string"
      ? entity.date
      : typeof entity["first-release-date"] === "string"
        ? entity["first-release-date"]
        : null;
  const status = typeof entity.status === "string" ? entity.status : null;
  const packaging = typeof entity.packaging === "string" ? entity.packaging : null;
  const barcode = typeof entity.barcode === "string" ? entity.barcode : null;
  const primaryType =
    typeof entity["primary-type"] === "string" ? entity["primary-type"] : null;
  const rating =
    isPlainObject(entity.rating) && typeof entity.rating.value === "number"
      ? `${entity.rating.value} (${typeof entity.rating["votes-count"] === "number" ? entity.rating["votes-count"] : "?"} votes)`
      : null;

  const tags = readTags(entity.tags);
  const genres = readTags(entity.genres);
  const aliases = entity.aliases;
  const relations = entity.relations;
  const releases = entity.releases;
  const recordings = entity.recordings;
  const annotation = entity.annotation;

  const knownKeys = new Set([
    "id",
    "name",
    "title",
    "type",
    "country",
    "disambiguation",
    "sort-name",
    "gender",
    "area",
    "begin-area",
    "end-area",
    "life-span",
    "aliases",
    "tags",
    "genres",
    "relations",
    "artist-credit",
    "release-group",
    "releases",
    "recordings",
    "media",
    "isrcs",
    "annotation",
    "rating",
    "length",
    "status",
    "packaging",
    "date",
    "barcode",
    "language",
    "script",
    "first-release-date",
    "primary-type",
    "secondary-types",
    "ipis",
    "isnis",
    "score",
    "video",
  ]);

  const extraEntries = Object.entries(entity).filter(([k, v]) => {
    if (knownKeys.has(k)) return false;
    if (v == null || v === "") return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  });

  return (
    <div className="space-y-1">
      {(name || disambig) && (
        <div className="pb-2">
          {name && <p className="text-base font-semibold text-text">{name}</p>}
          {disambig && <p className="text-xs text-muted">{disambig}</p>}
        </div>
      )}

      <div className="divide-y divide-line">
        <Fact label="Type">{type}</Fact>
        <Fact label="Primary type">{primaryType}</Fact>
        <Fact label="Country">{country}</Fact>
        <Fact label="Area">{area}</Fact>
        <Fact label="Begin area">{beginArea}</Fact>
        <Fact label="End area">{endArea}</Fact>
        <Fact label="Life span">{life}</Fact>
        <Fact label="Gender">{gender}</Fact>
        <Fact label="Artist credit">{credit}</Fact>
        <Fact label="Length">{length}</Fact>
        <Fact label="Date">{date}</Fact>
        <Fact label="Status">{status}</Fact>
        <Fact label="Packaging">{packaging}</Fact>
        <Fact label="Barcode">{barcode}</Fact>
        <Fact label="Rating">{rating}</Fact>
        {genres.length > 0 && (
          <Fact label="Genres">
            <ChipRow items={genres} />
          </Fact>
        )}
        {tags.length > 0 && (
          <Fact label="Tags">
            <ChipRow items={tags} />
          </Fact>
        )}
      </div>

      {annotation != null && (
        <CollapsibleSection title="Annotation" defaultOpen>
          <AnnotationText value={annotation} />
        </CollapsibleSection>
      )}

      {Array.isArray(aliases) && aliases.length > 0 && (
        <CollapsibleSection title="Aliases" count={aliases.length}>
          <AliasRows aliases={aliases} />
        </CollapsibleSection>
      )}

      {Array.isArray(relations) && relations.length > 0 && (
        <CollapsibleSection title="Relations" count={relations.length} triState>
          {(mode) => (
            <RelationRows relations={relations} expandAll={mode === "full"} />
          )}
        </CollapsibleSection>
      )}

      {Array.isArray(releases) && releases.length > 0 && (
        <CollapsibleSection title="Releases" count={releases.length}>
          <NestedEntityList items={releases} fallbackType="release" />
        </CollapsibleSection>
      )}

      {Array.isArray(recordings) && recordings.length > 0 && entityType !== "recording" && (
        <CollapsibleSection title="Recordings" count={recordings.length}>
          <NestedEntityList items={recordings} fallbackType="recording" />
        </CollapsibleSection>
      )}

      {isPlainObject(entity["release-group"]) && (
        <CollapsibleSection title="Release group" defaultOpen>
          <NestedEntityList items={[entity["release-group"]]} fallbackType="release-group" />
        </CollapsibleSection>
      )}

      {extraEntries.length > 0 && (
        <CollapsibleSection title="More fields" count={extraEntries.length}>
          <div className="divide-y divide-line">
            {extraEntries.map(([key, value]) => {
              if (Array.isArray(value) || isPlainObject(value)) {
                return (
                  <div key={key} className="py-1.5">
                    <p className="mb-1 text-xs text-faint">{humanLabel(key)}</p>
                    <pre
                      className={cn(
                        "max-h-40 overflow-auto rounded bg-panel-elevated px-2 py-1",
                        "text-left font-mono text-xs text-muted whitespace-pre-wrap",
                      )}
                    >
                      {JSON.stringify(value, null, 2)}
                    </pre>
                  </div>
                );
              }
              return (
                <Fact key={key} label={humanLabel(key)}>
                  {formatScalar(value)}
                </Fact>
              );
            })}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}
